// The client side of the idle timeout (plan §4.1: idle 30 min, warning at 25).
//
// The SERVER is the authority: it ends a session when its last non-peek
// request is older than the idle limit. The client only mirrors that clock so
// it can warn in time. Two rules keep the mirror honest:
//
//   1. Every non-peek API call counts as activity here, exactly as it does on
//      the server (api.ts records it), so the two clocks agree.
//   2. When the client's clock runs out it does NOT decide on its own. It asks
//      the server once (a peek, which never extends the session) and accepts
//      the answer: 401 ends the session; 200 means the server saw activity the
//      client did not, and the client re-syncs to the server's lastSeen.
//
// Rule 2 replaces a loop found in the M1 live walkthrough: the client ended
// the session locally, its own check found the session alive, the Users screen
// remounted and fetched again (refreshing the server), and the cycle repeated
// every two seconds, writing an audit row each time and keeping an unattended
// session alive indefinitely.

export type IdlePhase = "active" | "warn" | "expired";

export function idlePhase(nowMs: number, lastActivityMs: number, idleMs: number, warnBeforeMs: number): IdlePhase {
  const idleFor = nowMs - lastActivityMs;
  if (idleFor >= idleMs) return "expired";
  if (idleFor >= idleMs - warnBeforeMs) return "warn";
  return "active";
}

/** Seconds until the server would end the session, never negative. */
export function secondsLeft(nowMs: number, lastActivityMs: number, idleMs: number): number {
  return Math.max(0, Math.ceil((idleMs - (nowMs - lastActivityMs)) / 1000));
}

/**
 * Keepalive throttling WITHOUT losing the last activity (found in the M1 live
 * walkthrough, Phase 5): input is forwarded to the server at most once per
 * `throttleMs`, and input inside that window schedules ONE trailing keepalive
 * at the window's end. Without the trailing send, a user's final up-to-2
 * minutes of activity never reached the server, the next poll re-synced the
 * client to the server's earlier lastSeen, and the session ended that much
 * sooner than the user expected. With it the server hears about the final
 * activity at most `throttleMs` late.
 */
export function keepaliveAction(
  nowMs: number,
  lastSentMs: number,
  throttleMs: number,
  trailingPending: boolean
): { kind: "sendNow" } | { kind: "schedule"; atMs: number } | { kind: "none" } {
  if (nowMs - lastSentMs >= throttleMs) return { kind: "sendNow" };
  if (trailingPending) return { kind: "none" };
  return { kind: "schedule", atMs: lastSentMs + throttleMs };
}

/**
 * The server's lastSeen expressed on the client's clock: the client's "now"
 * minus how long ago, by the server's own clock, the server last saw activity.
 * Immune to any skew between the two clocks.
 */
export function syncedLastActivity(clientNowMs: number, serverNowMs: number, serverLastSeenMs: number): number {
  return clientNowMs - Math.max(0, serverNowMs - serverLastSeenMs);
}
